import {IOfferValue} from "@/store/slices/offerSlice";
import {Periodicidade} from "@/types/IOffer";
import {formataMoeda, IPrecoAnual} from "./precoAnual";


export interface IOfferHighlight {
    planName: string
    highlight: string
    extraTier: string
    bonus: string
    renew: string
    showCancel: boolean
}

interface IOfferHighlightInput {
    oferta: IOfferValue
    offer_type: string | null

    periodicidade: Periodicidade | null
    priceLabel: string
    precoAnual: IPrecoAnual
    escritorio: number
}

const ORDINAIS: [string, number][] = [
    ['decimo primeiro', 11],
    ['decimo segundo', 12],
    ['decimo terceiro', 13],
    ['decimo quarto', 14],
    ['decimo quinto', 15],
    ['decimo sexto', 16],
    ['decimo setimo', 17],
    ['decimo oitavo', 18],
    ['decimo nono', 19],
    ['vigesimo', 20],
    ['decimo', 10],
    ['primeiro', 1],
    ['segundo', 2],
    ['terceiro', 3],
    ['quarto', 4],
    ['quinto', 5],
    ['sexto', 6],
    ['setimo', 7],
    ['oitavo', 8],
    ['nono', 9]
]

const semAcento = (text: string): string =>
    text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

/** o legal vem com <b>, <a href> etc — tira a marcação antes de casar regex */
const semHtml = (text: string): string =>
    text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ')

/** "R$ 19,90 a partir do sétimo mês" -> 6 (a promo vai até o mês anterior) */
const mesesPeloAPartir = (aPartir: string): number | null => {
    if (!aPartir) {
        return null
    }

    const texto = semAcento(aPartir)
    if (!texto.includes('a partir do')) {
        return null
    }

    const numerico = texto.match(/a partir do\s+(\d+)\s*[ºo°]?\s*mes/)
    if (numerico) {
        return Number(numerico[1]) - 1
    }

    const encontrado = ORDINAIS.find(([palavra]) => texto.includes(`a partir do ${palavra} mes`))
    return encontrado ? encontrado[1] - 1 : null
}

interface IFaixas {
    primeiraMeses: number | null
    segundaMeses: number | null
    segundaValor: string | null
    finalValor: string | null
}


const extraiFaixas = (legal: string): IFaixas => {
    const texto = semAcento(semHtml(legal))

    const primeira = texto.match(/nos primeiros\s+(\d+)\s+(?:meses|mes)/)
        ?? texto.match(/nos\s+(\d+)\s+primeiros\s+(?:meses|mes)/)

    const segunda = texto.match(/e mais\s+(\d+)\s+(?:meses|mes)\s+por\s+r?\$?\s*([\d.,]+)/)

    const primeiroAno = texto.includes('no primeiro ano')
    const segundoAno = texto.match(/([\d.,]+)\s*\/?\s*m[êe]s\s+no segundo ano/)

    const final = texto.match(/demais parcelas\s*(?:de\s*)?r?\$?\s*([\d.,]+)/)

    return {
        primeiraMeses: primeira ? Number(primeira[1]) : (primeiroAno ? 12 : null),
        segundaMeses: segunda ? Number(segunda[1]) : (segundoAno ? 12 : null),
        segundaValor: segunda ? segunda[2] : (segundoAno ? segundoAno[1] : null),
        finalValor: final ? final[1] : null
    }
}

/** "garantido por 1 ano" / "garantido por 6 meses" */
const mesesPelaGarantia = (legal: string): number | null => {
    const texto = semAcento(semHtml(legal))

    const porAno = texto.match(/garantido por\s+(\d+)\s+anos?/)
    if (porAno) {
        return Number(porAno[1]) * 12
    }

    const porMes = texto.match(/garantido por\s+(\d+)\s+(?:mes|meses)/)
    if (porMes) {
        return Number(porMes[1])
    }

    return null
}

/** "R$30,00" / "30,00" -> "R$ 30,00" */
const normalizaMoeda = (value: string): string => {
    const texto = String(value).trim()
    if (!texto) {
        return ''
    }
    if (!texto.includes('R$')) {
        return `R$ ${texto}`
    }
    return texto.replace(/R\$\s*/, 'R$ ')
}

const valorDoBrinde = (brinde: string): string =>
    normalizaMoeda(brinde.replace(/^\s*voucher\s*(?:de\s*)?/i, ''))


const nomeSemVoucher = (name: string): string =>
    name.replace(/\s*\+\s*voucher\b.*$/i, '').trim()

const plural = (meses: number): string => meses === 1 ? 'mês' : 'meses'

const moedaParaNumero = (value: string): number | null => {
    const numero = Number(value.replace(/[^\d,]/g, '').replace(',', '.'))
    return Number.isFinite(numero) && numero > 0 ? numero : null
}

const renovacaoAnual = (valorMensal: string, parcelas: number | null): string => {
    if (parcelas === 12) {
        return `Após este período, 12x de ${valorMensal}`
    }

    const mensal = moedaParaNumero(valorMensal)
    if (mensal === null) {
        return ''
    }

    const ano = mensal * 12
    return parcelas
        ? `Após este período, ${parcelas}x de ${formataMoeda(ano / parcelas)}`
        : `Após este período, 1 ano por ${formataMoeda(ano)}`
}

const buildOfferHighlight = (input: IOfferHighlightInput): IOfferHighlight => {
    const {oferta, offer_type, periodicidade, priceLabel, precoAnual, escritorio} = input

    const ehPlanoAnual = periodicidade === 'anual' || periodicidade === 'anual_parcelada'

    const periodo = oferta?.valor?.periodo ?? '/mês'
    const escadinha = oferta?.escadinha?.[escritorio]
    const legal = oferta?.texto?.legal ?? ''
    const faixas = extraiFaixas(legal)

    const valorPromocional = oferta?.valor?.valor
    const valorPosterior = oferta?.valor_demais?.valor
    const temSegundaFaixa = faixas.segundaValor !== null
        || (typeof valorPosterior === 'number'
            && typeof valorPromocional === 'number'
            && valorPosterior !== valorPromocional)

    const promoMeses = faixas.primeiraMeses
        ?? mesesPeloAPartir(escadinha?.a_partir ?? '')
        ?? (temSegundaFaixa ? null : mesesPelaGarantia(legal))

    let highlight: string
    if (ehPlanoAnual) {
        highlight = precoAnual.parcelas
            ? `1 ano por ${precoAnual.parcelas}x de ${precoAnual.parcelaLabel}`
            : `1 ano por ${priceLabel}`
    } else if (promoMeses && promoMeses > 0) {
        highlight = `${promoMeses} ${plural(promoMeses)} por ${priceLabel}${periodo}`
    } else {
        highlight = `${priceLabel}${periodo}`
    }

    // 2ª faixa — só existe nas escadinhas de 3 degraus
    const extraTier = faixas.segundaMeses && faixas.segundaValor
        ? `+ ${faixas.segundaMeses} ${plural(faixas.segundaMeses)} por ${normalizaMoeda(faixas.segundaValor)}${periodo}`
        : ''

    const brinde = oferta?.brinde
    const valorBrinde = brinde ? valorDoBrinde(String(brinde)) : ''
    const bonus = valorBrinde ? `+ voucher de ${valorBrinde}` : ''

   
    let renew = ''
    const valorRenovacao = escadinha?.preco_capa_label
        ?? faixas.finalValor
        ?? escadinha?.valor_demais
        ?? oferta?.tabela_vigente?.sp?.label

    if (valorRenovacao) {
        const valor = normalizaMoeda(String(valorRenovacao))
       
        renew = ehPlanoAnual
            ? renovacaoAnual(valor, precoAnual.parcelas)
            : `Após este período, ${valor}${periodo}`
    } else if (semAcento(legal).includes('tabela vigente')) {
        renew = 'Após este período, o preço passa a ser da tabela vigente.'
    }

    const name = oferta?.name ? nomeSemVoucher(String(oferta.name)) : ''

    return {
        planName: name ? `Plano ${name}` : '',
        highlight,
        extraTier,
        bonus,
        renew,
        showCancel: offer_type !== 'impresso_digital'
    }
}

export default buildOfferHighlight
